import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { StatusBadge } from "@/components/StatusBadge";
import { formatDate } from "@/lib/formatters";
import { UsersRound, FileText, Package, ChevronDown, ChevronRight, ArrowRight, Mail, LayoutGrid, List } from "lucide-react";
import { OperatorWeeklyChart, WeeklyChartLegend } from "@/components/operators/OperatorWeeklyChart";
import { ReassignMenu } from "@/components/operators/ReassignMenu";
import { OperatorsMatrix } from "@/components/operators/OperatorsMatrix";

const VIEW_STORAGE_KEY = "operators.viewMode";

export default function Operators() {
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState("all"); // all | active | empty
  const [dayFilter, setDayFilter] = useState({}); // { [operatorId]: isoDate }
  const [viewMode, setViewMode] = useState(() => {
    if (typeof window === "undefined") return "list";
    return localStorage.getItem(VIEW_STORAGE_KEY) || "list";
  });
  const navigate = useNavigate();

  const switchView = (mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, mode);
    } catch (err) {
      // localStorage può fallire in modalità privata o quando lo storage è pieno.
      // Non è un errore critico: la modalità resta nello state per la sessione corrente.
      if (process.env.NODE_ENV !== "production") {
        console.warn("[Operators] Impossibile salvare la preferenza di vista:", err?.message);
      }
    }
  };

  const handleDayClick = (operatorId, isoDate) => {
    setDayFilter(prev => {
      const current = prev[operatorId];
      if (current === isoDate) {
        const { [operatorId]: _omit, ...rest } = prev;
        return rest;
      }
      return { ...prev, [operatorId]: isoDate };
    });
    setExpandedId(operatorId);
  };

  const formatDayLabel = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const dow = ["Domenica", "Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato"][d.getDay()];
    return `${dow} ${d.getDate()}/${String(d.getMonth() + 1).padStart(2, "0")}`;
  };

  const loadData = () => api.get("/operators/workload")
    .then(({ data }) => setOperators(data || []))
    .catch(() => toast.error("Errore nel caricamento degli operatori"))
    .finally(() => setLoading(false));

  useEffect(() => { loadData(); }, []);

  const filtered = operators.filter(op => {
    if (filter === "active") return op.total_count > 0;
    if (filter === "empty") return op.total_count === 0;
    return true;
  });

  const totalAssignments = operators.reduce((s, o) => s + o.total_count, 0);
  const activeOperators = operators.filter(o => o.total_count > 0).length;

  if (loading) return <div className="p-8 text-zinc-400 text-sm">Caricamento...</div>;

  return (
    <div className="space-y-6 animate-in fade-in duration-300" data-testid="operators-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter font-['Outfit']">Operatori</h1>
          <p className="text-sm text-zinc-500 mt-1">Lavori assegnati a ciascun operatore dell'organizzazione</p>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <WeeklyChartLegend />
          <div className="text-zinc-500"><span className="font-mono font-semibold text-zinc-900">{operators.length}</span> operatori · <span className="font-mono font-semibold text-zinc-900">{activeOperators}</span> attivi · <span className="font-mono font-semibold text-zinc-900">{totalAssignments}</span> assegnazioni</div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-xs">
          {viewMode === "list" && [
            { key: "all", label: "Tutti" },
            { key: "active", label: "Con lavori" },
            { key: "empty", label: "Senza lavori" },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`operators-filter-${f.key}`}
              className={`px-3 py-1.5 rounded-sm border transition ${filter === f.key ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50"}`}
            >{f.label}</button>
          ))}
        </div>
        <div className="hidden md:inline-flex items-center rounded-sm border border-zinc-200 bg-white p-0.5" data-testid="operators-view-toggle">
          <button onClick={() => switchView("list")} data-testid="operators-view-list" className={`px-2.5 py-1 rounded-sm text-xs flex items-center gap-1 transition ${viewMode === "list" ? "bg-[#002FA7] text-white" : "text-zinc-600 hover:bg-zinc-50"}`}>
            <List className="w-3.5 h-3.5" /> Lista
          </button>
          <button onClick={() => switchView("matrix")} data-testid="operators-view-matrix" className={`px-2.5 py-1 rounded-sm text-xs flex items-center gap-1 transition ${viewMode === "matrix" ? "bg-[#002FA7] text-white" : "text-zinc-600 hover:bg-zinc-50"}`}>
            <LayoutGrid className="w-3.5 h-3.5" /> Tabella settimanale
          </button>
        </div>
      </div>

      {viewMode === "matrix" ? (
        <OperatorsMatrix operators={operators} onReassigned={loadData} />
      ) : filtered.length === 0 ? (
        <div className="card-surface p-10 text-center text-zinc-400 text-sm">Nessun operatore corrisponde al filtro selezionato.</div>
      ) : (
        <div className="space-y-3">
          {filtered.map(op => {
            const isExpanded = expandedId === op.id;
            const canExpand = op.total_count > 0;
            return (
              <div key={op.id} className="card-surface overflow-hidden" data-testid={`operator-card-${op.id}`}>
                <button
                  onClick={() => canExpand && setExpandedId(isExpanded ? null : op.id)}
                  disabled={!canExpand}
                  data-testid={`operator-toggle-${op.id}`}
                  className={`w-full px-5 py-4 flex items-center gap-4 text-left transition ${canExpand ? "hover:bg-zinc-50" : "cursor-default"}`}
                >
                  <div className="w-10 h-10 rounded-full bg-blue-50 text-[#002FA7] flex items-center justify-center shrink-0">
                    <UsersRound className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">{op.name || "Senza nome"}</span>
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-zinc-100 text-zinc-600">{op.role_name || "Operatore"}</span>
                      {!op.is_active && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-red-50 text-red-700 border border-red-200">Disattivato</span>}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-zinc-500 mt-0.5"><Mail className="w-3 h-3" /><span className="truncate">{op.email}</span></div>
                  </div>
                  <div className="hidden sm:flex items-center gap-3 text-xs text-zinc-500">
                    <span className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> <span className="font-mono font-semibold text-zinc-900">{op.quotes_count}</span> preventivi</span>
                    <span className="flex items-center gap-1.5"><Package className="w-3.5 h-3.5" /> <span className="font-mono font-semibold text-zinc-900">{op.inventories_count}</span> inventari</span>
                  </div>
                  <OperatorWeeklyChart
                    operator={op}
                    selectedDay={dayFilter[op.id]}
                    onDayClick={(iso) => handleDayClick(op.id, iso)}
                  />
                  {canExpand && (isExpanded ? <ChevronDown className="w-4 h-4 text-zinc-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-zinc-400 shrink-0" />)}
                </button>

                {isExpanded && (() => {
                  const day = dayFilter[op.id];
                  const matches = (item) => !day || item.pickup_date === day || item.delivery_date === day;
                  const filteredQuotes = op.quotes.filter(matches);
                  const filteredInventories = op.inventories.filter(matches);
                  return (
                  <div className="border-t border-zinc-100 px-5 py-4 bg-zinc-50/50 space-y-4" data-testid={`operator-details-${op.id}`}>
                    {day && (
                      <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-sm bg-blue-50/60 border border-[#002FA7]/30 text-xs" data-testid={`operator-day-filter-${op.id}`}>
                        <div className="text-[#002FA7]">Filtrato per: <strong>{formatDayLabel(day)}</strong> · {filteredQuotes.length + filteredInventories.length} eventi</div>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDayClick(op.id, day); }}
                          data-testid={`operator-clear-day-filter-${op.id}`}
                          className="text-[#002FA7] hover:underline font-medium"
                        >Rimuovi filtro</button>
                      </div>
                    )}
                    <div className="sm:hidden text-xs text-zinc-500">
                      {op.quotes_count} preventivi · {op.inventories_count} inventari
                    </div>
                    {filteredQuotes.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold mb-2">Preventivi ({filteredQuotes.length}{day ? ` di ${op.quotes.length}` : ""})</div>
                        <div className="space-y-1.5">
                          {filteredQuotes.map(q => (
                            <div key={q.id} className="w-full px-3 py-2 rounded-sm border border-zinc-200 bg-white hover:bg-blue-50/30 transition flex items-center gap-2">
                              <FileText className="w-4 h-4 text-[#002FA7] shrink-0" />
                              <button
                                onClick={() => navigate(`/quotes/${q.id}`)}
                                data-testid={`operator-quote-${q.id}`}
                                className="flex-1 min-w-0 text-left"
                              >
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="font-mono text-xs">{q.number || "BOZZA"}</span>
                                  <StatusBadge status={q.status} />
                                  {q.delivery_type === "deposito" && <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 rounded-sm bg-amber-50 text-amber-700 border border-amber-300">Deposito</span>}
                                </div>
                                <div className="text-xs text-zinc-500 mt-0.5 truncate">
                                  {q.customer_name || "—"}{q.subject ? ` · ${q.subject}` : ""}{q.pickup_date ? ` · ${formatDate(q.pickup_date)}` : ""}
                                </div>
                              </button>
                              <ReassignMenu docId={q.id} docType="quote" currentOperatorId={op.id} operators={operators} onReassigned={loadData} />
                              <ArrowRight className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {filteredInventories.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold mb-2">Inventari ({filteredInventories.length}{day ? ` di ${op.inventories.length}` : ""})</div>
                        <div className="space-y-1.5">
                          {filteredInventories.map(inv => (
                            <div key={inv.id} className="w-full px-3 py-2 rounded-sm border border-zinc-200 bg-white hover:bg-blue-50/30 transition flex items-center gap-2">
                              <Package className="w-4 h-4 text-[#7C3AED] shrink-0" />
                              <button
                                onClick={() => navigate(`/inventories/${inv.id}`)}
                                data-testid={`operator-inventory-${inv.id}`}
                                className="flex-1 min-w-0 text-left"
                              >
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="font-mono text-xs">{inv.number || "BOZZA"}</span>
                                  <StatusBadge status={inv.status} />
                                  {inv.inventory_type === "release" && <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 rounded-sm bg-amber-50 text-amber-700 border border-amber-300">Riconsegna</span>}
                                </div>
                                <div className="text-xs text-zinc-500 mt-0.5 truncate">
                                  {inv.customer_name || "—"}{inv.pickup_date ? ` · ${formatDate(inv.pickup_date)}` : ""}{inv.delivery_date && inv.delivery_date !== inv.pickup_date ? ` → ${formatDate(inv.delivery_date)}` : ""}
                                </div>
                              </button>
                              <ReassignMenu docId={inv.id} docType="inventory" currentOperatorId={op.id} operators={operators} onReassigned={loadData} />
                              <ArrowRight className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {day && filteredQuotes.length === 0 && filteredInventories.length === 0 && (
                      <div className="text-center text-zinc-400 text-xs py-3" data-testid={`operator-day-empty-${op.id}`}>Nessun lavoro per questo giorno</div>
                    )}
                  </div>
                  );
                })()}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
